/**
 * Library cache — persistent metadata for the user's imported music.
 *
 * Two IndexedDB object stores:
 *   - `tracks`: one row per track metadata (title, artist, size, path...).
 *              Does NOT store audio bytes (that would be tens of GB for a
 *              real library and get evicted under storage pressure — the
 *              user picked "metadata only" in the initial spec).
 *   - `handles`: stores the File System Access API DirectoryHandle so we
 *              can re-scan the same folder on future visits without
 *              re-prompting the user for a path. Handle objects are
 *              structured-cloneable and IDB can store them directly.
 *              Not supported on iOS/Safari — those platforms fall through
 *              to the folder-picker path.
 *
 * The library is device-local: no server round-trip, no user account
 * scoping. Uses the DEFAULT device store, keyed under a single DB name.
 */

const DB_NAME = 'mixgenie-library';
const DB_VERSION = 1;
const TRACKS_STORE = 'tracks';
const HANDLES_STORE = 'handles';

export interface LibraryTrackMeta {
  id: string;                 // stable — sha256 of (relativePath + size + lastModified)
  name: string;               // filename with extension
  title?: string;             // parsed from ID3 or filename
  artist?: string;
  album?: string;
  size: number;               // bytes
  mimeType?: string;
  relativePath: string;       // within the imported folder, e.g. "Rock/track.mp3"
  lastModified: number;       // File.lastModified (ms since epoch)
  importedAt: number;
  lastPlayedAt?: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TRACKS_STORE)) {
        db.createObjectStore(TRACKS_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(HANDLES_STORE)) {
        db.createObjectStore(HANDLES_STORE);
      }
    };
  });
}

function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function readAllTracks(): Promise<LibraryTrackMeta[]> {
  try {
    const db = await openDb();
    const tx = db.transaction(TRACKS_STORE, 'readonly');
    const all = await req(tx.objectStore(TRACKS_STORE).getAll());
    db.close();
    return (all || []) as LibraryTrackMeta[];
  } catch {
    return [];
  }
}

export async function writeTracks(tracks: LibraryTrackMeta[]): Promise<void> {
  if (!tracks.length) return;
  try {
    const db = await openDb();
    const tx = db.transaction(TRACKS_STORE, 'readwrite');
    const store = tx.objectStore(TRACKS_STORE);
    for (const t of tracks) store.put(t);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch { /* best effort */ }
}

export async function deleteTrack(id: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(TRACKS_STORE, 'readwrite');
    await req(tx.objectStore(TRACKS_STORE).delete(id));
    db.close();
  } catch { /* best effort */ }
}

export async function clearAllTracks(): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(TRACKS_STORE, 'readwrite');
    await req(tx.objectStore(TRACKS_STORE).clear());
    db.close();
  } catch { /* best effort */ }
}

// ---------------------------------------------------------------------------
// FileSystemDirectoryHandle persistence (Chrome/Edge desktop + Android Chrome).
// Safari/iOS: `showDirectoryPicker` doesn't exist → we skip this branch and the
// user is on the multi-file picker.
// ---------------------------------------------------------------------------

const HANDLE_KEY = 'library-dir';

export async function saveDirHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(HANDLES_STORE, 'readwrite');
    await req(tx.objectStore(HANDLES_STORE).put(handle, HANDLE_KEY));
    db.close();
  } catch { /* best effort */ }
}

export async function readDirHandle(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const db = await openDb();
    const tx = db.transaction(HANDLES_STORE, 'readonly');
    const h = await req<FileSystemDirectoryHandle>(tx.objectStore(HANDLES_STORE).get(HANDLE_KEY));
    db.close();
    return h || null;
  } catch {
    return null;
  }
}

export async function clearDirHandle(): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(HANDLES_STORE, 'readwrite');
    await req(tx.objectStore(HANDLES_STORE).delete(HANDLE_KEY));
    db.close();
  } catch { /* best effort */ }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Stable ID that survives re-scans of the same folder. Same relative
 * path + size + lastModified → same id. That means a rename-in-place
 * looks like a new track (which matches the user mental model — they
 * renamed the file, we treat it as new).
 */
export async function computeTrackId(relativePath: string, size: number, lastModified: number): Promise<string> {
  const input = `${relativePath}::${size}::${lastModified}`;
  if (typeof crypto?.subtle?.digest !== 'function') {
    // Very old browser fallback: djb2 hash. Non-cryptographic but stable
    // for our "same-file → same-id" needs.
    let h = 5381;
    for (let i = 0; i < input.length; i++) h = ((h << 5) + h) ^ input.charCodeAt(i);
    return `f_${(h >>> 0).toString(36)}_${size.toString(36)}`;
  }
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  const arr = Array.from(new Uint8Array(buf));
  return arr.slice(0, 12).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const AUDIO_EXT = /\.(mp3|m4a|aac|wav|ogg|flac|opus|webm)$/i;

export function isAudioFile(fileName: string, mimeType?: string): boolean {
  return AUDIO_EXT.test(fileName) || (!!mimeType && mimeType.startsWith('audio/'));
}

/**
 * Recursively read every audio file out of a FileSystemDirectoryHandle,
 * yielding {file, relativePath} tuples. Skips hidden files/folders (.git
 * etc.), symlink loops (browsers don't expose them), and non-audio.
 */
export async function* iterAudioFilesFromHandle(
  dir: FileSystemDirectoryHandle,
  prefix = '',
): AsyncGenerator<{ file: File; relativePath: string }> {
  // @ts-expect-error: entries() is on the current spec but not always typed
  for await (const [name, handle] of dir.entries() as AsyncIterable<[string, FileSystemHandle]>) {
    if (name.startsWith('.')) continue;
    const rel = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === 'file') {
      try {
        const file = await (handle as FileSystemFileHandle).getFile();
        if (isAudioFile(file.name, file.type)) {
          yield { file, relativePath: rel };
        }
      } catch { /* permission race, skip */ }
    } else if (handle.kind === 'directory') {
      yield* iterAudioFilesFromHandle(handle as FileSystemDirectoryHandle, rel);
    }
  }
}

/**
 * Very cheap "Artist - Title" filename parser. Real ID3 extraction is a
 * larger project (see task follow-up); this covers the ~80% of downloaded
 * music that ships with a "Artist - Title.mp3" convention.
 */
export function parseFileName(fileName: string): { title: string; artist?: string } {
  const stem = fileName.replace(/\.[^.]+$/, '').trim();
  const m = stem.match(/^(.+?)\s*[-–—]\s*(.+)$/);
  if (m) return { artist: m[1].trim(), title: m[2].trim() };
  return { title: stem };
}
